import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Alert, Autocomplete, ComboboxParsedItem, Text } from '@mantine/core'
import { email } from '@sideway/address'

import { ErrorAlert } from '#components/Alerts'
import { FaIcon } from '#components/FaIcon'
import { ModalShell, useModalControls } from '#components/Modals'
import { NavAnchor } from '#components/NavAnchor'
import { IOrg, useMst } from '#state'

import { useInviteSeat } from './useInviteSeat'
import { useSeatCapacity } from './useSeatCapacity'
import {
  seatChangeFailure,
  SeatLimitError,
  useSeatChanges,
} from './useSeatChanges'

// See (../../Modals/readme.md)
// oxlint-disable-next-line react/only-export-components
const CreateInvite = ({ org }: { org: IOrg }) => {
  const { doDebug } = useMst()
  const controls = useModalControls()
  const capacity = useSeatCapacity(org).data
  const { invite } = useSeatChanges(org)
  const freeInvitees = React.useMemo(
    () => capacity?.freeInvitees ?? [],
    [capacity],
  )
  const nameByEmail = React.useMemo(
    () =>
      new Map(freeInvitees.map((person) => [person.email, person.name ?? ''])),
    [freeInvitees],
  )
  // The typed text can hold a name while the person searches, so the raw text
  // stays in state and only the address goes to the api.
  const [searchText, setSearchText] = React.useState('')
  const [seatLimit, setSeatLimit] = React.useState<{
    seatMax: number
    taken: number
  } | null>(null)
  const trimmedEmail = searchText.trim()
  const { notice, claim, setNeeded } = useInviteSeat(org, trimmedEmail)

  const handleSubmit = async () => {
    controls.setLoading(true)
    setSeatLimit(null)
    try {
      await doDebug()
      await invite.mutateAsync({ email: trimmedEmail, claim })
      controls.onClose()
    } catch (e) {
      if (e instanceof SeatLimitError) {
        if (e.limit.seatMode === 'static') {
          setNeeded(e.limit.needed)
        } else {
          setSeatLimit({ seatMax: e.limit.seatMax ?? 0, taken: e.limit.taken })
        }
      } else {
        const { message, hostedInvoiceUrl } = seatChangeFailure(
          e,
          'Failed to send invitation',
        )
        if (hostedInvoiceUrl) window.open(hostedInvoiceUrl, '_blank')
        controls.setErrorMessage(message)
      }
    }
    controls.setLoading(false)
  }

  return (
    <ModalShell
      size="sm"
      title="Invite new member"
      opened={controls.opened}
      onClose={controls.onClose}
      onConfirm={handleSubmit}
      confirmLabel="Create Invitation"
      cancelLabel="Cancel"
      disabled={!email.isValid(trimmedEmail)}
      loading={controls.loading}
    >
      <Autocomplete
        clearable
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="off"
        placeholder="name@email.com"
        value={searchText}
        onChange={setSearchText}
        data={
          freeInvitees.length > 0
            ? [
                {
                  group: 'Invite people from other workspaces.',
                  items: freeInvitees.map((person) => person.email),
                },
              ]
            : []
        }
        limit={8}
        // The typed text can be a name or an email address, so both are matched
        // here. The default filter reads the email address alone.
        filter={({ options, search }) => {
          const query = search.trim().toLowerCase()
          const matches = (address: string) => {
            const name = nameByEmail.get(address) ?? ''
            return (
              address.toLowerCase().includes(query) ||
              name.toLowerCase().includes(query)
            )
          }

          const filtered: ComboboxParsedItem[] = []
          for (const option of options) {
            if (!('group' in option)) {
              if (matches(String(option.value))) filtered.push(option)
              continue
            }
            const items = option.items.filter((item) =>
              matches(String(item.value)),
            )
            if (items.length > 0) filtered.push({ ...option, items })
          }

          return filtered
        }}
        renderOption={({ option }) => {
          const address = String(option.value)
          const name = nameByEmail.get(address)
          return (
            <div>
              <Text size="sm">{name || address}</Text>
              <Text size="xs" c="dimmed">
                {name ? address : 'Invited to another workspace'}
              </Text>
            </div>
          )
        }}
      />
      {notice && (
        <Alert
          color={notice.color}
          icon={<FaIcon icon="fa-credit-card" />}
          styles={{ title: { color: 'var(--mantine-color-text)' } }}
          title="Billing notice"
        >
          <Text size="sm">{notice.text}</Text>
          {notice.detail ? (
            <Text size="xs" c="dimmed" mt={6}>
              {notice.detail}
            </Text>
          ) : null}
        </Alert>
      )}
      {controls.errorMessage && <ErrorAlert title={controls.errorMessage} />}
      {seatLimit && (
        <Alert
          color="red"
          icon={<FaIcon icon="fa-exclamation-triangle" />}
          title={`You've reached your ${seatLimit.seatMax}-user limit`}
        >
          <Text size="sm">
            Your subscription includes {seatLimit.seatMax} users, and all{' '}
            {seatLimit.taken} seats are currently in use. You can add anyone who
            already has access to your subscription, but adding a new user will
            require an{' '}
            <NavAnchor href="/settings/org" onClick={controls.onClose}>
              upgrade
            </NavAnchor>
            .
          </Text>
        </Alert>
      )}
    </ModalShell>
  )
}
const CreateInviteModal = NiceModal.create(CreateInvite)

export const showCreateInviteModal = (props: { org: IOrg }) =>
  NiceModal.show(CreateInviteModal, props)
